import { Injectable, inject, signal } from '@angular/core';
import { escapeRegExp } from 'lodash';
import { BehaviorSubject, NEVER, Observable, Subject, map } from 'rxjs';
import { DisplaySlotLocalizer } from './display-slot-localizer';
import { SlotRegistry } from './slot-registry';
import { truthyOnly } from '../../util/core';
import { LOGGER, Logger } from '../../util/logger';
import { TranslationManager, TranslationType, Translator } from '../../util/translation-manager';
import { Slot } from '../core/slot';
import { ParamSystemDefRecord, SlotLocalizationDef, SlotLocalizationSetDef, slotLocalizationDefToMulti } from '../util/param-system-def';

class SlotL10n {
    name: string;
    base: Slot[];
    selections: { name: string; to: Slot[] }[];

    private indexSubj = new BehaviorSubject<number>(0);

    constructor(reg: SlotRegistry, def: SlotLocalizationDef) {
        const { name, base, selections } = slotLocalizationDefToMulti(def);

        if (!selections.every((s) => s.to.length === base.length)) {
            throw new Error('SLOT localization ' + name + ' has inconsistent canonical and display array lengths');
        }

        this.name = name;
        this.base = base.map(reg.slotByKey);
        this.selections = selections.map((selDef) => ({
            name: selDef.name,
            to: selDef.to.map(reg.slotByKey),
        }));
    }

    get index() {
        return this.indexSubj.value;
    }

    set index(value) {
        this.indexSubj.next(value);
    }

    get indexObs() {
        return this.indexSubj.asObservable();
    }
}

class SlotL10nSet {
    name: string;
    codes: string[];
    selections = new Map<SlotL10n, number>();

    constructor(reg: SlotRegistry, l10ns: Array<SlotL10n>, def: SlotLocalizationSetDef) {
        this.name = def.name;
        this.codes = def.codes || [];

        for (const key of Object.keys(def.map)) {
            const canonicalSlot = reg.slotByKey(key);
            const localizedSlot = reg.slotByKey(def.map[key]);

            // now find a SLOT localization with this mapping
            const l10n = l10ns.find((l) => l.base[0] === canonicalSlot);
            if (!l10n) {
                throw new Error('SLOT localization not found for ' + key + ' in localization set ' + this.name);
            }
            const index = l10n.selections.findIndex((s) => s.to[0] === localizedSlot);
            if (index < 0) {
                throw new Error('SLOT localization index not found for ' + def.map[key] + ' in localization set ' + this.name);
            }

            this.selections.set(l10n, index);
        }
    }
}

@Injectable({ providedIn: 'root' })
export class ParamDefDisplaySlotLocalizer implements DisplaySlotLocalizer, Translator {
    private l10ns = new Array<SlotL10n>();
    private l10nSets = new Array<SlotL10nSet>();
    private change = new Subject<void>();
    private readonly logger = inject<Logger>(LOGGER);
    private readonly reg = inject(SlotRegistry);
    private readonly translationManager = inject(TranslationManager);

    private slotMap = new Map<Slot, { l10n: SlotL10n; index: number }>();
    private readySubj = new BehaviorSubject<ParamSystemDefRecord | undefined>(undefined);
    private indicesInt: Record<string, number> = {};
    private selectionVersion = signal(0);
    private translationRegexes = signal<
        { regex: RegExp; reverseRegex: RegExp; replacement: string; reverseReplacement: string; len: number }[]
    >([]);

    constructor() {
        this.translationManager.add(this);
        this.reg.ready().subscribe((rec) => {
            this.l10ns = rec.def.slotLocalizations.map((l10nDef) => new SlotL10n(this.reg, l10nDef));
            this.l10nSets = rec.def.slotLocalizationSets.map((setDef) => new SlotL10nSet(this.reg, this.l10ns, setDef));
            for (const l10n of this.l10ns) {
                for (let i = 0; i < l10n.base.length; ++i) {
                    const slot = l10n.base[i];
                    if (this.slotMap.has(slot)) {
                        throw new Error(`Multiple localizations for canonical slot ${this.reg.keyFromSlot(slot)}`);
                    }
                    this.slotMap.set(slot, { l10n, index: i });
                }
            }

            if (this.l10nSets.length === 0) {
                this.logger.info(`No localization sets available in param def`);
            }
            try {
                const languages = (navigator.languages || []).length ? navigator.languages : [navigator.language];
                const countryCode = languages
                    .map((lang) => {
                        const match = /^[a-z]{2}[-_]([a-z]{2})$/i.exec(lang);
                        return match ? match[1] : undefined;
                    })
                    .filter((v) => v)[0];
                if (!countryCode) {
                    throw `No country code extracted from [${languages.join(', ')}], cannot select localization set`;
                }
                let matchingSet = this.l10nSets.find((set) => set.codes.includes(countryCode.toLowerCase()));
                if (matchingSet) {
                    this.logger.info(`Matched country code ${countryCode}, selecting localization set ${matchingSet.name}`);
                } else {
                    matchingSet = this.l10nSets.find((set) => set.codes.includes('default'));
                    if (matchingSet) {
                        this.logger.info(
                            `No match found for country code ${countryCode}, falling back to default localization set ${matchingSet.name}`,
                        );
                    } else {
                        throw `No match found for country code ${countryCode} and no default found, not selecting any localization set`;
                    }
                }
                matchingSet.selections.forEach((index, l10n) => (l10n.index = index));
            } catch (err) {
                this.logger.warn(err);
            }

            this.updateFromIndices();
            this.updateTranslators();
            this.readySubj.next(rec);
            this.notifyChange();
        });
    }

    ready() {
        return this.readySubj.pipe(truthyOnly());
    }

    get localizations(): { name: string; entries: string[]; index: number }[] {
        return this.l10ns.map((l) => ({
            name: l.name,
            entries: l.selections.map((s) => s.name),
            index: l.index,
        }));
    }

    get localizationSets(): string[] {
        return this.l10nSets.map((s) => s.name);
    }

    get localizationSet(): string | undefined {
        const activeMap = new Map<SlotL10n, number>();
        for (const l10n of this.l10ns) {
            activeMap.set(l10n, l10n.index);
        }

        for (const set of this.l10nSets) {
            const l10ns = Array.from(set.selections.keys());
            if (l10ns.every((l10n) => activeMap.get(l10n) === set.selections.get(l10n))) {
                return set.name;
            }
        }

        return undefined;
    }

    set localizationSet(name: string | undefined) {
        const set = this.l10nSets.find((s) => s.name === name);
        if (set) {
            let changed = false;
            set.selections.forEach((index, l10n) => {
                if (l10n.index !== index) {
                    changed = true;
                    l10n.index = index;
                    this.indicesInt[l10n.name] = index;
                }
            });
            if (changed) {
                this.updateTranslators();
                this.notifyChange();
            }
        }
    }

    get localizationsChangeObs() {
        // used to save user selections to persistent store
        return this.change.asObservable();
    }

    get indices() {
        return this.indicesInt;
    }

    set indices(value) {
        this.indicesInt = value;
        this.updateFromIndices();
    }

    setIndex(name: string, index: number) {
        const l10n = this.l10ns.find((l) => l.name === name);

        if (l10n && index >= 0 && index < l10n.selections.length && index !== l10n.index) {
            l10n.index = index;
            this.indicesInt[name] = index;
            this.updateTranslators();
            this.notifyChange();
        }
    }

    localize(canonicalSlot: Slot): Slot {
        // Register a reactive dependency so callers inside computed()/effect() re-run when the unit
        // selection changes. localize() returns a different Slot instance per selection (units are not
        // mutated in place), so the version signal is what makes that swap observable to signals.
        this.selectionVersion();
        const entry = this.slotMap.get(canonicalSlot);
        if (entry) {
            const selection = entry.l10n.selections[entry.l10n.index];
            return selection.to[entry.index];
        } else {
            return canonicalSlot;
        }
    }

    changeObs(canonicalSlot: Slot): Observable<void> {
        const entry = this.slotMap.get(canonicalSlot);
        if (entry) {
            return entry.l10n.indexObs.pipe(map((_) => undefined));
        } else {
            return NEVER;
        }
    }

    translate(str: string, type: TranslationType) {
        if (!str) return str;
        let out = str;
        const regexes = this.translationRegexes(); // register signal dependency
        for (const tr of regexes) {
            out = out.replace(tr.regex, tr.replacement);
        }
        return out;
    }

    cislate(str: string, type: TranslationType) {
        if (!str) return str;
        let out = str;
        const regexes = this.translationRegexes(); // register signal dependency
        for (const tr of regexes) {
            out = out.replace(tr.reverseRegex, tr.reverseReplacement);
        }
        return out;
    }

    private notifyChange() {
        // Bump the signal (for signal-based consumers via localize()) and fire the Subject (for RxJS
        // consumers). Keep these two together: any selection change must reach both worlds.
        this.selectionVersion.update((v) => v + 1);
        this.change.next();
    }

    private updateFromIndices() {
        for (const key of Object.keys(this.indicesInt)) {
            const index = this.indicesInt[key];
            if (typeof key === 'string' && typeof index === 'number') {
                this.setIndex(key, this.indicesInt[key]);
            }
        }
    }

    private updateTranslators() {
        const list: { regex: RegExp; reverseRegex: RegExp; replacement: string; reverseReplacement: string; len: number }[] = [];
        for (const [canonicalSlot, entry] of this.slotMap.entries()) {
            const selection = entry.l10n.selections[entry.l10n.index];
            const localizedSlot = selection.to[entry.index];
            const canonicalKey = this.reg.keyFromSlot(canonicalSlot);
            const localizedKey = this.reg.keyFromSlot(localizedSlot);

            if (canonicalKey && localizedKey) {
                list.push({
                    regex: new RegExp(escapeRegExp(`{{${canonicalKey}}}`), 'g'),
                    reverseRegex: new RegExp(escapeRegExp(localizedKey), 'g'),
                    replacement: localizedKey,
                    reverseReplacement: `{{${canonicalKey}}}`,
                    len: canonicalKey.length,
                });
            }
        }
        list.sort((a, b) => b.len - a.len);
        this.translationRegexes.set(list);
    }
}
